import {FC, useState} from 'react'
import {useDispatch} from 'react-redux'
import {FieldPath, SubmitHandler, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {Section} from 'jbc-front/components/presenters/form/Section'
import {Label} from 'jbc-front/components/presenters/form/Label'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import Button from 'jbc-front/components/Button'
import {eachValidationErrorsForHookFrom, ServerValidationError} from 'hooks/graphql/error/ServerValidationError'
import {asyncError} from 'store/actions/asyncError'
import {CustomEmail, DefaultEmail} from '../query'
import {schema, Schema} from '../schema'
import {Preview} from './Preview'
import styles from '../Presenter.scss'

interface FormProps {
  title: string
  defaultEmail: DefaultEmail
  customEmail?: CustomEmail
  onSubmit: SubmitHandler<Schema>
}

export const Form: FC<FormProps> = ({title, defaultEmail, customEmail, onSubmit: onSubmitProp}) => {
  const dispatch = useDispatch()
  const [isOpenPreview, setIsOpenPreview] = useState(false)
  const {
    register,
    watch,
    handleSubmit,
    setError,
    formState: {isSubmitting, errors},
  } = useForm<Schema>({
    defaultValues: {
      subject: customEmail?.subject,
      body: customEmail?.body,
    },
    resolver: yupResolver(schema),
  })

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    try {
      await onSubmitProp(data)
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema, data, (path, error) => {
          setError(path as FieldPath<Schema>, error)
        })
        window.scrollTo({top: 0, behavior: 'smooth'})
        return
      }
      dispatch(asyncError(err))
      return
    }
  }

  return (
    <>
      <Section>
        <div className="u-mb20">
          <Label>件名</Label>
          <Spacer direction="y" size={8} />
          <Input {...register('subject')} placeholder={defaultEmail.subject} isError={!!errors.subject?.message} />
          {errors.subject?.message && <ErrorMessage>{errors.subject.message}</ErrorMessage>}
        </div>
        <div>
          <Label>デフォルト本文</Label>
          <Spacer direction="y" size={8} />
          <div className={styles.body}>
            <p dangerouslySetInnerHTML={{__html: defaultEmail.header || ''}} />
          </div>
        </div>
        <div className="u-mb20">
          <Label>カスタマイズ文</Label>
          <Spacer direction="y" size={8} />
          <Textarea {...register('body')} isError={!!errors.body?.message} />
          {errors.body?.message && <ErrorMessage>{errors.body.message}</ErrorMessage>}
        </div>
        デフォルトの本文は編集できません。
        <div className={styles.help}>
          <ul>
            <li>
              {'<b>'}と{'</b>'}の間に文字を入れると太字になります。
            </li>
            <li>
              {'<u>'}と{'</u>'}の間に文字を入れると下線が付きます。
            </li>
          </ul>
        </div>
      </Section>
      <div className={styles.bottomButtonsArea}>
        <Button onClick={() => setIsOpenPreview(true)}>プレビュー</Button>
        <Button primary disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
          保存
        </Button>
      </div>
      <Preview
        isOpen={isOpenPreview}
        onClosePreview={() => setIsOpenPreview(false)}
        title={title}
        defaultEmail={defaultEmail}
        customEmail={{subject: watch('subject'), body: watch('body')}}
      />
    </>
  )
}
